/**
 * Shared service-commitment policy constants and helpers.
 *
 * These are used by both `provider-rate-cards` (RateCardConfiguration) and
 * `provider-profile` (AddRateCardModal, mapRateCardToFormValues,
 * buildCreateServiceCommitmentPayload).
 */

// Inter-Leg Hold Policy options
export const INTER_LEG_POLICY_OPTIONS = [
  { label: "No charge between legs - Gap isn't billed", value: 'none' },
  { label: 'Charge wait time - Driver holds between legs', value: 'hold' },
  {
    label: 'Charge deadhead miles - Driver repositions between legs',
    value: 'unloadedMileageToOrigin',
  },
];

// Map API policy shape → dropdown value
export const getInterLegPolicyValue = (policyWrapper) => {
  // API returns { policy: { kind, assumes } } or { policy: null } or null/undefined
  const policy = policyWrapper?.policy ?? policyWrapper;
  if (!policy) return 'none';
  if (policy.kind === 'constant' && policy.assumes === 'hold') return 'hold';
  if (
    policy.kind === 'constant' &&
    policy.assumes === 'unloadedMileageToOrigin'
  )
    return 'unloadedMileageToOrigin';
  return 'none';
};

// Map dropdown value → API payload for CREATE endpoint
export const getInterLegPolicyPayload = (value) => {
  if (value === 'hold') return { kind: 'constant', assumes: 'hold' };
  if (value === 'unloadedMileageToOrigin')
    return { kind: 'constant', assumes: 'unloadedMileageToOrigin' };
  return null;
};

/**
 * The two pricing value sources a line item can be priced with. These are the
 * user-facing *intent*; each resolves to a full priority order below.
 *
 * `assumed` is the API's name for what the UI calls "Estimated" — there is no
 * `estimated` value in the backend vocabulary.
 */
export const EVIDENCE_SELECTION_SOURCES = Object.freeze({
  ATTESTED: 'attested',
  ESTIMATED: 'assumed',
});

/**
 * Source a line item starts on when nothing has been set for it — a new rate
 * card, or a fact ID the API returned no policy for.
 *
 * Estimated, changed from attested when per-line-item sources landed. Note this
 * differs from the source a conflicting grouped pair reads back as; see
 * {@link getEvidenceSelectionPolicyValues}.
 */
const DEFAULT_EVIDENCE_SELECTION_SOURCE = EVIDENCE_SELECTION_SOURCES.ESTIMATED;

/** The label each source shows in the UI. */
export const EVIDENCE_SELECTION_SOURCE_LABELS = Object.freeze({
  [EVIDENCE_SELECTION_SOURCES.ATTESTED]: 'Attested values',
  [EVIDENCE_SELECTION_SOURCES.ESTIMATED]: 'Estimated values',
});

/** Rows of the two-level panel, estimated first per the rate card mockup. */
export const PRICING_VALUE_SOURCE_PANEL_OPTIONS = Object.freeze(
  [
    EVIDENCE_SELECTION_SOURCES.ESTIMATED,
    EVIDENCE_SELECTION_SOURCES.ATTESTED,
  ].map((value) =>
    Object.freeze({ label: EVIDENCE_SELECTION_SOURCE_LABELS[value], value }),
  ),
);

/** Short forms used in the collapsed summary, e.g. "Estimated · 2 attested". */
const SOURCE_SHORT_LABEL = Object.freeze({
  [EVIDENCE_SELECTION_SOURCES.ATTESTED]: 'Attested',
  [EVIDENCE_SELECTION_SOURCES.ESTIMATED]: 'Estimated',
});

/**
 * Fallback priority order each source resolves to. This ordering is settled and
 * is not user-configurable — the UI only picks which of the two applies.
 */
const EVIDENCE_SELECTION_ORDERS = Object.freeze({
  [EVIDENCE_SELECTION_SOURCES.ATTESTED]: Object.freeze([
    'postAuthorized',
    'preAuthorized',
    'attested',
  ]),
  [EVIDENCE_SELECTION_SOURCES.ESTIMATED]: Object.freeze([
    'preAuthorized',
    'assumed',
    'attested',
  ]),
});

/**
 * The five controls the UI offers over the eight backend fact IDs.
 *
 * The grouping is a presentation decision, not a backend constraint: the API
 * accepts all eight fact IDs independently, so splitting a pair later (origin
 * stairs attested, destination estimated) needs no data migration — only a new
 * entry here.
 *
 * A group covering more than one fact ID carries a `hint` naming which two it
 * writes: the control's own label cannot say that, and a user moving Attendants
 * to attested is moving adult and child companions together whether they know
 * it or not. Kept beside `factIds` so the two stay honest about each other.
 */
export const PRICING_VALUE_SOURCE_GROUPS = Object.freeze([
  Object.freeze({
    key: 'loadedMiles',
    label: 'Loaded miles',
    factIds: Object.freeze(['loadedGroundRoadTraversalInMiles']),
  }),
  Object.freeze({
    key: 'unloadedMiles',
    label: 'Unloaded miles',
    factIds: Object.freeze([
      'unloadedGroundRoadTraversalToOriginInMiles',
      'unloadedGroundRoadTraversalFromDestinationInMiles',
    ]),
    hint: 'Covers the empty miles driven to the pickup and from the drop-off.',
  }),
  Object.freeze({
    key: 'waitTime',
    label: 'Wait time',
    factIds: Object.freeze(['interLegVoluntaryHoldDurationInMinutes']),
  }),
  Object.freeze({
    key: 'steps',
    label: 'Steps',
    factIds: Object.freeze([
      'stairCountNavigatedAtOrigin',
      'stairCountNavigatedAtDestination',
    ]),
    hint: 'Covers the stairs at the pickup and at the drop-off.',
  }),
  Object.freeze({
    key: 'attendants',
    label: 'Attendants',
    factIds: Object.freeze([
      'patientAdultCompanionCount',
      'patientChildCompanionCount',
    ]),
    hint: 'Covers both adult and child companions.',
  }),
]);

/** The eight fact IDs the groups above cover, in the order they appear. */
export const EVIDENCE_SELECTION_FACT_IDS = Object.freeze(
  PRICING_VALUE_SOURCE_GROUPS.flatMap((group) => group.factIds),
);

/**
 * The other of the two sources. With exactly two, unchecking a line item under
 * one source is the same statement as checking it under the other, which is how
 * a click on an already-checked item stays meaningful.
 */
export const getOppositeEvidenceSelectionSource = (source) =>
  source === EVIDENCE_SELECTION_SOURCES.ATTESTED
    ? EVIDENCE_SELECTION_SOURCES.ESTIMATED
    : EVIDENCE_SELECTION_SOURCES.ATTESTED;

/** Every line item on one source, used for defaults and Select all. */
export const buildUniformPricingValueSources = (
  source = DEFAULT_EVIDENCE_SELECTION_SOURCE,
) =>
  PRICING_VALUE_SOURCE_GROUPS.reduce(
    (acc, group) => ({ ...acc, [group.key]: source }),
    {},
  );

/** Resolve a priority order back to the source that produced it. */
export const getEvidenceSelectionSourceFromOrder = (order) => {
  const winner = Array.isArray(order) ? order[0] : undefined;
  if (winner === 'assumed' || winner === 'preAuthorized')
    return EVIDENCE_SELECTION_SOURCES.ESTIMATED;
  if (winner === 'attested' || winner === 'postAuthorized')
    return EVIDENCE_SELECTION_SOURCES.ATTESTED;
  return DEFAULT_EVIDENCE_SELECTION_SOURCE;
};

/**
 * Normalize any of the shapes the API returns into a source per fact ID.
 *
 * Accepts:
 *  1. `{ policies: [{ factId, policy: { order } }] }` (wrapped array)
 *  2. `[{ factId, policy: { order } }]` (plain array)
 *  3. `{ [factId]: { kind, order } }` (flat map, from GET commitment)
 *  4. null/undefined
 *
 * Every fact ID is always present in the result; ones the API said nothing
 * about fall back to {@link DEFAULT_EVIDENCE_SELECTION_SOURCE}.
 *
 * @param {Object|Array|null} policiesWrapper - Raw `evidenceSelectionPolicies`.
 * @returns {Object<string, string>} Source keyed by fact ID (all eight).
 */
const getEvidenceSelectionSourcesByFactId = (policiesWrapper) => {
  const sources = EVIDENCE_SELECTION_FACT_IDS.reduce(
    (acc, factId) => ({ ...acc, [factId]: DEFAULT_EVIDENCE_SELECTION_SOURCE }),
    {},
  );

  if (!policiesWrapper) return sources;

  const entries = Array.isArray(policiesWrapper)
    ? policiesWrapper
    : policiesWrapper.policies;

  if (Array.isArray(entries)) {
    entries.forEach((entry) => {
      if (entry?.factId && entry.factId in sources) {
        sources[entry.factId] = getEvidenceSelectionSourceFromOrder(
          entry.policy?.order,
        );
      }
    });
    return sources;
  }

  // Flat map keyed by fact ID.
  if (typeof policiesWrapper === 'object') {
    EVIDENCE_SELECTION_FACT_IDS.forEach((factId) => {
      const policy = policiesWrapper[factId];
      if (policy?.order)
        sources[factId] = getEvidenceSelectionSourceFromOrder(policy.order);
    });
  }

  return sources;
};

/**
 * Collapse the eight per-fact sources into the five values the UI edits.
 *
 * A grouped pair whose two fact IDs disagree cannot be produced by this UI, but
 * the API allows it. Such a group reads back as attested — the conservative
 * choice for a field that drives the billable amount — rather than silently
 * presenting the cheaper source as if it were settled.
 *
 * @param {Object|Array|null} policiesWrapper - Raw `evidenceSelectionPolicies`.
 * @returns {Object<string, string>} Source keyed by group key (all five).
 */
export const getEvidenceSelectionPolicyValues = (policiesWrapper) => {
  const byFactId = getEvidenceSelectionSourcesByFactId(policiesWrapper);

  return PRICING_VALUE_SOURCE_GROUPS.reduce((acc, group) => {
    const groupSources = group.factIds.map((factId) => byFactId[factId]);
    const isUniform = groupSources.every(
      (source) => source === groupSources[0],
    );
    return {
      ...acc,
      [group.key]: isUniform
        ? groupSources[0]
        : EVIDENCE_SELECTION_SOURCES.ATTESTED,
    };
  }, {});
};

/**
 * Fan the five UI values out to all eight fact IDs.
 *
 * Every fact ID is sent on every save — never a partial — so the backend never
 * has to interpret an absent field.
 *
 * @param {Object<string, string>} pricingValueSources - Source per group key.
 * @returns {Array<{factId: string, policy: Object}>} One entry per fact ID.
 */
export const getEvidenceSelectionPolicyPayload = (pricingValueSources = {}) =>
  PRICING_VALUE_SOURCE_GROUPS.flatMap((group) => {
    const source =
      pricingValueSources[group.key] ?? DEFAULT_EVIDENCE_SELECTION_SOURCE;
    const order =
      EVIDENCE_SELECTION_ORDERS[source] ??
      EVIDENCE_SELECTION_ORDERS[DEFAULT_EVIDENCE_SELECTION_SOURCE];

    return group.factIds.map((factId) => ({
      factId,
      policy: { kind: 'priorityOrder', order: [...order] },
    }));
  });

/**
 * How many groups sit on each source. Drives the count badges on the parent
 * rows, which are what surface a mixed configuration without opening a flyout.
 *
 * @param {Object<string, string>} pricingValueSources - Source per group key.
 * @returns {{attested: number, assumed: number}} Group counts per source.
 */
export const getPricingValueSourceCounts = (pricingValueSources = {}) =>
  PRICING_VALUE_SOURCE_GROUPS.reduce(
    (counts, group) => {
      const source =
        pricingValueSources[group.key] ?? DEFAULT_EVIDENCE_SELECTION_SOURCE;
      return { ...counts, [source]: counts[source] + 1 };
    },
    {
      [EVIDENCE_SELECTION_SOURCES.ATTESTED]: 0,
      [EVIDENCE_SELECTION_SOURCES.ESTIMATED]: 0,
    },
  );

/**
 * Collapsed trigger text: the plain option label when every line item shares a
 * source, otherwise the majority source plus the minority count
 * (e.g. "Estimated · 2 attested").
 *
 * The group count is odd, so a majority always exists — there is no tie to
 * break.
 *
 * @param {Object<string, string>} pricingValueSources - Source per group key.
 * @returns {string} Text for the collapsed field.
 */
export const getPricingValueSourceSummary = (pricingValueSources = {}) => {
  const counts = getPricingValueSourceCounts(pricingValueSources);
  const { ATTESTED, ESTIMATED } = EVIDENCE_SELECTION_SOURCES;

  if (counts[ESTIMATED] === 0 || counts[ATTESTED] === 0) {
    const uniformSource = counts[ESTIMATED] === 0 ? ATTESTED : ESTIMATED;
    return EVIDENCE_SELECTION_SOURCE_LABELS[uniformSource];
  }

  const [majority, minority] =
    counts[ESTIMATED] > counts[ATTESTED]
      ? [ESTIMATED, ATTESTED]
      : [ATTESTED, ESTIMATED];

  return `${SOURCE_SHORT_LABEL[majority]} · ${counts[minority]} ${SOURCE_SHORT_LABEL[minority].toLowerCase()}`;
};

/**
 * Mileage rounding — "Round Miles to Whole Number" on a rate configuration's
 * charge method section. Maps to `tierCollection.roundingPolicy.roundingMode`.
 *
 * Only the loaded and unloaded mileage sections expose this; every other
 * section stays on the API default of 'none'.
 */
export const ROUND_MILES_MODES = Object.freeze({
  UP: 'ceiling',
  DOWN: 'floor',
  NONE: 'none',
});

/** The default option — 'none' is also the API's default roundingMode. */
export const DEFAULT_ROUND_MILES_OPTION = Object.freeze({
  id: ROUND_MILES_MODES.NONE,
  label: 'No rounding',
  summary: 'bill miles as measured.',
});

/**
 * `summary` trails the label in the dropdown's option rows; the closed field
 * shows the bare label. "No rounding" leads because it's the default.
 */
export const ROUND_MILES_OPTIONS = Object.freeze([
  DEFAULT_ROUND_MILES_OPTION,
  Object.freeze({
    id: ROUND_MILES_MODES.UP,
    label: 'Round up',
    summary: 'raise billed miles to the next whole mile.',
  }),
  Object.freeze({
    id: ROUND_MILES_MODES.DOWN,
    label: 'Round down',
    summary: 'drop billed miles to the previous whole mile.',
  }),
]);

/**
 * Billing method — "Billing method" on a mileage bands configuration. Maps to
 * `tierCollection.accumulationPolicy`.
 *
 * Bands are a lookup rather than a graduated scale: the total distance picks
 * one band, and this decides how much of the distance that band's rate covers.
 * Only the mileage sections expose it; base rate, oxygen, steps and assistance
 * bands stay on the marginal policy they have always been written with.
 */
export const BAND_BILLING_METHODS = Object.freeze({
  FULL_DISTANCE: 'billFullDistance',
  REMAINDER: 'billRemainder',
});

/**
 * `summary` trails the label in the dropdown's option rows; the closed field
 * shows the bare label. There is no default — a new card has to choose.
 */
export const BAND_BILLING_METHOD_OPTIONS = Object.freeze([
  Object.freeze({
    id: BAND_BILLING_METHODS.FULL_DISTANCE,
    label: 'Bill full distance',
    summary: "all miles charge at the matched band's rate.",
  }),
  Object.freeze({
    id: BAND_BILLING_METHODS.REMAINDER,
    label: 'Bill remainder',
    summary: "only miles above the matched band's lower bound charge.",
  }),
]);

/**
 * Map the dropdown selection -> `tierCollection.accumulationPolicy`. Billing
 * the remainder is what marginal accumulation does; billing the full distance
 * is flat. A section that exposes no billing method keeps 'marginal'.
 */
export const getBandAccumulationPolicyPayload = (billingMethod) => {
  if (!billingMethod) return 'marginal';
  return billingMethod.id === BAND_BILLING_METHODS.REMAINDER
    ? 'marginal'
    : 'flat';
};

/** Map `tierCollection.accumulationPolicy` -> the dropdown option. */
export const getBandBillingMethodOption = (accumulationPolicy) => {
  const id =
    accumulationPolicy === 'marginal'
      ? BAND_BILLING_METHODS.REMAINDER
      : BAND_BILLING_METHODS.FULL_DISTANCE;
  return BAND_BILLING_METHOD_OPTIONS.find((opt) => opt.id === id);
};

/** Caption under the "Round Miles to Whole Number" field. */
export const ROUND_MILES_HELPER_TEXT =
  "Rounds each leg's measured miles to a whole mile before rates apply. Leave as No rounding to bill as measured.";

/**
 * Map the dropdown selection → `roundingPolicy.roundingMode`.
 * Anything unrecognized (including no selection) falls back to 'none'.
 */
export const getRoundingModePayload = (roundMiles) => {
  const mode = roundMiles?.id;
  return Object.values(ROUND_MILES_MODES).includes(mode)
    ? mode
    : ROUND_MILES_MODES.NONE;
};

/** Map `roundingPolicy.roundingMode` → the dropdown option. */
export const getRoundMilesOption = (roundingMode) =>
  ROUND_MILES_OPTIONS.find((opt) => opt.id === roundingMode) ||
  DEFAULT_ROUND_MILES_OPTION;
